import { z } from 'zod';
import { DateOnlySchema, daysBetween, type DateOnly } from '../date-only.ts';

/**
 * The company's own sales-tax exemption certificate (SCRUM-312).
 *
 * A contractor with an exemption buys materials with no sales tax and
 * charges tax when it sells them on. The supplier only leaves the tax
 * off if it holds a certificate, and the certificate is what the
 * supplier's auditor asks for years later — so "we bought this exempt"
 * is a claim about a piece of paper, and the paper has a number and
 * usually an end date.
 *
 * **One certificate per company, on the company document.** Not a
 * collection: a builder has one exemption, and modelling it as a list
 * would make "which one applies to this bill" a question somebody has
 * to answer on every purchase. If a company ever needs two (a second
 * state), that is a new field or a collection then, with a reason.
 *
 * **Nothing here is a secret.** A certificate number is handed to every
 * supplier the company buys from; it is an identifier, not a
 * credential, which is why it can sit in a document any member can
 * read.
 *
 * What this is **not**: permission to sell exempt. Selling without tax
 * to an exempt customer needs that customer's certificate, which is a
 * different record on a different party — SCRUM-276, and deliberately
 * not built yet.
 */
export const SalesTaxExemptionSchema = z.object({
  /**
   * The number the supplier writes on the invoice. In Arizona this is
   * the Transaction Privilege Tax licence number; elsewhere a resale or
   * exemption certificate number. Not validated against a format: every
   * state issues a different one and a regex here would refuse a valid
   * certificate.
   */
  number: z.string().trim().min(1).max(80),
  /** Who issued it: "Arizona", "Arizona (Mohave County)". */
  jurisdiction: z.string().trim().max(120).optional(),
  /** The form a supplier asks for by name, such as "Arizona Form 5000A". */
  form: z.string().trim().max(120).optional(),
  issuedOn: DateOnlySchema.optional(),
  /**
   * When it stops being good.
   *
   * Optional, and absent means no end date rather than unknown: a
   * blanket certificate with no expiry is ordinary, and treating absence
   * as a problem would warn on every bill for a company that has one.
   */
  expiresOn: DateOnlySchema.optional(),
  note: z.string().trim().max(2_000).optional(),
});
export type SalesTaxExemption = z.infer<typeof SalesTaxExemptionSchema>;

/**
 * How much notice is given before a certificate lapses.
 *
 * A renewal goes through a state office, so a week is not enough time
 * to act on and a year would mean the warning is permanent furniture.
 */
export const EXEMPTION_EXPIRY_NOTICE_DAYS = 30;

export type ExemptionStanding = 'none' | 'valid' | 'expiring' | 'expired';

/**
 * Whether the company could buy exempt on a given date.
 *
 * **Asked as of a date, not as of today**, and that is the whole point
 * of the signature: a bill dated in March was bought under whatever
 * certificate was good in March. Checking a two-year-old purchase
 * against today's expiry would flag every bill the company ever filed,
 * which is how a warning becomes wallpaper.
 */
export function exemptionStanding(
  exemption: SalesTaxExemption | undefined,
  on: DateOnly,
): ExemptionStanding {
  if (!exemption || exemption.number.trim() === '') return 'none';
  if (!exemption.expiresOn) return 'valid';
  const left = daysBetween(on, exemption.expiresOn);
  // Valid *through* its expiry date: a certificate dated 31 December is
  // good on 31 December, the way a driving licence is.
  if (left < 0) return 'expired';
  return left <= EXEMPTION_EXPIRY_NOTICE_DAYS ? 'expiring' : 'valid';
}

/**
 * What to say about a claimed exemption, or `null` when there is
 * nothing to say.
 *
 * Here rather than in the screen because both the bill and the settings
 * page have to say the same thing, and because the sentence is the
 * feature: "no certificate on file" is the finding an auditor makes, and
 * the company pays the tax plus a penalty for it.
 */
export function exemptionProblem(standing: ExemptionStanding): string | null {
  switch (standing) {
    case 'none':
      return 'No exemption certificate is on file. A supplier that leaves the tax off still has to hold one, so record it under Settings → Tax rates.';
    case 'expired':
      return 'The exemption certificate had expired by this date. A purchase made under a lapsed certificate is taxable, and the tax lands back on the company.';
    case 'expiring':
      return 'The exemption certificate expires within a month. Renewing it late makes every purchase after that date taxable.';
    default:
      return null;
  }
}

/** `TPT-20481234 · Arizona · expires 2027-06-30`, for a readback. */
export function formatExemption(exemption: SalesTaxExemption): string {
  return [
    exemption.number,
    exemption.jurisdiction,
    exemption.expiresOn ? `expires ${exemption.expiresOn}` : undefined,
  ]
    .filter((part): part is string => !!part && part !== '')
    .join(' · ');
}
